import React, { CSSProperties } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './Box.module.css';
import {
  backgroundColorVariants,
  type BackgroundVariants,
} from '../../utils/backgroundColor/backgroundColor';
import {
  flexVariants,
  isFlexBasisToken,
  resolveFlexBasisCssValue,
  type FlexBasisToken,
  type FlexVariants,
} from '../../utils/flex/flex';
import flexStyles from '../../utils/flex/flex.module.css';
import paddingStyles from '../../utils/padding/padding.module.css';
import marginStyles from '../../utils/margin/margin.module.css';
import { borderVariants, type BorderVariants } from '../../utils/border/border';
import {
  paddingVariants,
  type PaddingVariants,
} from '../../utils/padding/padding';
import { marginVariants, type MarginVariants } from '../../utils/margin/margin';
import {
  positionVariants,
  type PositionVariants,
} from '../../utils/position/position';
import {
  getBaseValueOrUndefined,
  generateResponsiveClasses,
  type WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';
import { responsiveToCssVars } from '../../utils/breakpoint/responsiveToCssVars';

const getResponsiveClassesFlex = generateResponsiveClasses(flexStyles);
const getResponsiveClassesPadding = generateResponsiveClasses(paddingStyles);
const getResponsiveClassesMargin = generateResponsiveClasses(marginStyles);

const listVariants = cva('', {
  variants: {
    as: {
      li: styles['box__list-item'],
    },
  },
});

const warnedDeprecations = new Set<string>();
const boxSizingVariants = cva('', {
  variants: {
    boxSizing: {
      'content-box': styles['box-sizing--content-box'],
      'border-box': styles['box-sizing--border-box'],
      inherit: styles['box-sizing--inherit'],
      initial: styles['box-sizing--initial'],
      revert: styles['box-sizing--revert'],
      'revert-layer': styles['box-sizing--revert-layer'],
      unset: styles['box-sizing--unset'],
    },
  },
});

type BoxSizingVariants = VariantProps<typeof boxSizingVariants>;
type BoxSizingValue = NonNullable<BoxSizingVariants['boxSizing']>;

const deprecatedBorderRadiusProps: Array<[string, string[]]> = [
  ['borderTopRadius', ['borderRadiusTopLeft', 'borderRadiusTopRight']],
  ['borderBottomRadius', ['borderRadiusBottomLeft', 'borderRadiusBottomRight']],
  ['borderLeftRadius', ['borderRadiusTopLeft', 'borderRadiusBottomLeft']],
  ['borderRightRadius', ['borderRadiusTopRight', 'borderRadiusBottomRight']],
];

export interface BoxBaseProps
  extends BackgroundVariants,
    Omit<FlexVariants, 'flexBasis'>,
    BorderVariants,
    PaddingVariants,
    MarginVariants,
    PositionVariants,
    Omit<React.HTMLAttributes<Element>, 'className' | 'style'> {
  /**
   * @deprecated Use `borderRadiusTopLeft` and `borderRadiusTopRight` instead.
   */
  borderTopRadius?: BorderVariants['borderTopRadius'];
  /**
   * @deprecated Use `borderRadiusBottomLeft` and `borderRadiusBottomRight` instead.
   */
  borderBottomRadius?: BorderVariants['borderBottomRadius'];
  /**
   * @deprecated Use `borderRadiusTopLeft` and `borderRadiusBottomLeft` instead.
   */
  borderLeftRadius?: BorderVariants['borderLeftRadius'];
  /**
   * @deprecated Use `borderRadiusTopRight` and `borderRadiusBottomRight` instead.
   */
  borderRightRadius?: BorderVariants['borderRightRadius'];
  /**
   * The HTML element to render the Box as.
   */
  as?:
    | 'div'
    | 'article'
    | 'aside'
    | 'nav'
    | 'section'
    | 'main'
    | 'span'
    | 'ul'
    | 'ol'
    | 'li';
  /**
   * The width of the Box.
   * Accepts any valid CSS unit or "full"
   */
  width?: 'full' | string;
  /**
   * **[Responsive]** The maximum width of the Box.
   * Accepts any valid CSS unit or "pageWidth" to use the `section-container-max-width` token.
   */
  maxWidth?: 'pageWidth' | string;
  /**
   * **[Responsive]** The minimum width of the Box.
   * Accepts any valid CSS unit.
   */
  minWidth?: string;
  /**
   * The height of the Box.
   * Accepts any valid CSS unit.
   */
  height?: string;
  /**
   * **[Responsive]** The maximum height of the Box.
   * Accepts any valid CSS unit.
   */
  maxHeight?: string;
  /**
   * The minimum height of the Box.
   * Accepts any valid CSS unit.
   */
  minHeight?: string;
  /**
   * The z-index of the Box.
   * Accepts any valid CSS z-index value.
   */
  zIndex?: string;
  /**
   * The top position of the Box.
   * Accepts any valid CSS position value.
   */
  top?: string;
  /**
   * The right position of the Box.
   * Accepts any valid CSS position value.
   */
  right?: string;
  /**
   * The bottom position of the Box.
   * Accepts any valid CSS position value.
   */
  bottom?: string;
  /**
   * The left position of the Box.
   * Accepts any valid CSS position value.
   */
  left?: string;
  /**
   * Test identifier for testing purposes.
   * Defaults to "box" if not provided.
   */
  'data-testid'?: string;
  /**
   * @deprecated Use `data-testid` instead.
   * @ignore
   */
  dataTestId?: string;
  /**
   * CSS overflow-wrap property.
   */
  overflowWrap?: 'normal' | 'break-word' | 'anywhere';
  /**
   * CSS word-break property.
   */
  wordBreak?: React.CSSProperties['wordBreak'];
  /**
   * **[Responsive]** Defines the default size of a flex item before remaining space is distributed.
   * Accepts predefined flex-basis tokens and arbitrary CSS values (e.g. "200px", "30%", "clamp(200px, 50vw, 400px)").
   */
  flexBasis?: FlexBasisToken | string | number;
  /**
   * CSS box-sizing property.
   */
  boxSizing?: BoxSizingValue;
}

type ResponsivePropKeys =
  | 'justifyContent'
  | 'flexDirection'
  | 'gap'
  | 'rowGap'
  | 'columnGap'
  | 'flexBasis'
  | 'alignItems'
  | 'paddingTop'
  | 'paddingRight'
  | 'paddingBottom'
  | 'paddingLeft'
  | 'padding'
  | 'paddingX'
  | 'paddingY'
  | 'marginTop'
  | 'marginRight'
  | 'marginBottom'
  | 'marginLeft'
  | 'margin'
  | 'marginX'
  | 'marginY'
  | 'minWidth'
  | 'maxWidth'
  | 'maxHeight';

export type BoxProps = WithResponsiveProps<BoxBaseProps, ResponsivePropKeys>;

export const Box = ({
  children,
  as: Component = 'div',
  width,
  height,
  minHeight,
  zIndex,
  top,
  right,
  bottom,
  left,
  position,
  'data-testid': dataTestIdNative,
  dataTestId: dataTestIdLegacy = 'box',
  overflowWrap,
  wordBreak,
  boxSizing,
  ...props
}: BoxProps) => {
  // Deprecation warnings for side-based border radius props
  if (process.env.NODE_ENV !== 'production') {
    deprecatedBorderRadiusProps.forEach(([prop, replacements]) => {
      const typedProp = prop as keyof typeof props;
      if (
        typedProp in props &&
        props[typedProp] !== undefined &&
        !warnedDeprecations.has(prop)
      ) {
        warnedDeprecations.add(prop);
        console.warn(
          `[Box] The prop "${prop}" is deprecated. Please use ${replacements
            .map((replacement) => `"${replacement}"`)
            .join(' and ')} instead.`,
        );
      }
    });
  }

  //Responsive Props

  const baseJustifyContent = getBaseValueOrUndefined(props.justifyContent);
  const baseFlexDirection = getBaseValueOrUndefined(props.flexDirection);
  const baseGap = getBaseValueOrUndefined(props.gap);
  const baseRowGap = getBaseValueOrUndefined(props.rowGap);
  const baseColumnGap = getBaseValueOrUndefined(props.columnGap);
  const baseAlignItems = getBaseValueOrUndefined(props.alignItems);
  const baseDisplay = getBaseValueOrUndefined(props.display);
  const baseFlexWrap = getBaseValueOrUndefined(props.flexWrap);
  const baseFlexGrow = getBaseValueOrUndefined(props.flexGrow);
  const baseFlexShrink = getBaseValueOrUndefined(props.flexShrink);
  const baseFlexBasis = getBaseValueOrUndefined(props.flexBasis);
  const basePaddingTop = getBaseValueOrUndefined(props.paddingTop);
  const basePaddingRight = getBaseValueOrUndefined(props.paddingRight);
  const basePaddingBottom = getBaseValueOrUndefined(props.paddingBottom);
  const basePaddingLeft = getBaseValueOrUndefined(props.paddingLeft);
  const basePadding = getBaseValueOrUndefined(props.padding);
  const basePaddingX = getBaseValueOrUndefined(props.paddingX);
  const basePaddingY = getBaseValueOrUndefined(props.paddingY);
  const baseMarginTop = getBaseValueOrUndefined(props.marginTop);
  const baseMarginRight = getBaseValueOrUndefined(props.marginRight);
  const baseMarginBottom = getBaseValueOrUndefined(props.marginBottom);
  const baseMarginLeft = getBaseValueOrUndefined(props.marginLeft);
  const baseMargin = getBaseValueOrUndefined(props.margin);
  const baseMarginX = getBaseValueOrUndefined(props.marginX);
  const baseMarginY = getBaseValueOrUndefined(props.marginY);

  const maxWidthVars = responsiveToCssVars(props.maxWidth, {
    varPrefix: '--box-max-width',
    normalise: (value) => {
      if (value === 'pageWidth') {
        return 'var(--alto-section-container-max-width)';
      }
      return value; // arbitrary CSS string (e.g. "960px", "80vw", "min(960px, 100%)")
    },
  });

  const maxHeightVars = responsiveToCssVars(props.maxHeight, {
    varPrefix: '--box-max-height',
    normalise: (value) => value, // arbitrary CSS string (e.g. "500px", "80vh", "min(500px, 100%)")
  });

  const minWidthVars = responsiveToCssVars(props.minWidth, {
    varPrefix: '--box-min-width',
    normalise: (value) => value, // arbitrary CSS string (e.g. "300px", "50vw", "min(300px, 100%)")
  });

  const flexBasisVars = responsiveToCssVars(props.flexBasis, {
    varPrefix: '--box-flex-basis',
    normalise: (value) => resolveFlexBasisCssValue(value),
  });

  const flexBaseClass = flexVariants({
    display: baseDisplay,
    justifyContent: baseJustifyContent,
    flexDirection: baseFlexDirection,
    gap: baseGap,
    rowGap: baseRowGap,
    columnGap: baseColumnGap,
    alignItems: baseAlignItems,
    flexWrap: baseFlexWrap,
    flexGrow: baseFlexGrow,
    flexShrink: baseFlexShrink,
    flexBasis: isFlexBasisToken(baseFlexBasis) ? baseFlexBasis : undefined,
  });

  const paddingBaseClass = paddingVariants({
    paddingTop: basePaddingTop,
    paddingRight: basePaddingRight,
    paddingBottom: basePaddingBottom,
    paddingLeft: basePaddingLeft,
    padding: basePadding,
    paddingX: basePaddingX,
    paddingY: basePaddingY,
  });

  const marginBaseClass = marginVariants({
    marginTop: baseMarginTop,
    marginRight: baseMarginRight,
    marginBottom: baseMarginBottom,
    marginLeft: baseMarginLeft,
    margin: baseMargin,
    marginX: baseMarginX,
    marginY: baseMarginY,
  });

  const justifyContentResponsiveClasses = getResponsiveClassesFlex(
    props.justifyContent,
    'flex-justify-content',
  );

  const flexDirectionResponsiveClasses = getResponsiveClassesFlex(
    props.flexDirection,
    'flex-direction',
  );

  const gapResponsiveClasses = getResponsiveClassesFlex(props.gap, 'flex-gap');

  const rowGapResponsiveClasses = getResponsiveClassesFlex(
    props.rowGap,
    'flex-row-gap',
  );

  const columnGapResponsiveClasses = getResponsiveClassesFlex(
    props.columnGap,
    'flex-column-gap',
  );

  const alignItemsResponsiveClasses = getResponsiveClassesFlex(
    props.alignItems,
    'flex-align-items',
  );

  const paddingTopResponsiveClasses = getResponsiveClassesPadding(
    props.paddingTop,
    'padding-top',
  );

  const paddingRightResponsiveClasses = getResponsiveClassesPadding(
    props.paddingRight,
    'padding-right',
  );

  const paddingBottomResponsiveClasses = getResponsiveClassesPadding(
    props.paddingBottom,
    'padding-bottom',
  );

  const paddingLeftResponsiveClasses = getResponsiveClassesPadding(
    props.paddingLeft,
    'padding-left',
  );

  const paddingResponsiveClasses = getResponsiveClassesPadding(
    props.padding,
    'padding',
  );

  const paddingXResponsiveClasses = getResponsiveClassesPadding(
    props.paddingX,
    'padding-x',
  );

  const paddingYResponsiveClasses = getResponsiveClassesPadding(
    props.paddingY,
    'padding-y',
  );

  const marginTopResponsiveClasses = getResponsiveClassesMargin(
    props.marginTop,
    'margin-top',
  );

  const marginRightResponsiveClasses = getResponsiveClassesMargin(
    props.marginRight,
    'margin-right',
  );

  const marginBottomResponsiveClasses = getResponsiveClassesMargin(
    props.marginBottom,
    'margin-bottom',
  );

  const marginLeftResponsiveClasses = getResponsiveClassesMargin(
    props.marginLeft,
    'margin-left',
  );

  const marginResponsiveClasses = getResponsiveClassesMargin(
    props.margin,
    'margin',
  );

  const marginXResponsiveClasses = getResponsiveClassesMargin(
    props.marginX,
    'margin-x',
  );

  const marginYResponsiveClasses = getResponsiveClassesMargin(
    props.marginY,
    'margin-y',
  );

  // Keep existing convenience behavior for all-sides borders:
  // if a border colour is set but width is omitted, default width to `sm`.
  const borderWidth =
    !props?.borderWidth &&
    props.borderColour !== undefined &&
    props.borderColour !== 'none'
      ? 'sm'
      : props.borderWidth;

  // Mirror the same convenience behavior for top-only borders.
  // If top colour is set without top width, default top width to `sm`.
  const borderTopWidth =
    !props?.borderTopWidth &&
    props.borderTopColour !== undefined &&
    props.borderTopColour !== 'none'
      ? 'sm'
      : props.borderTopWidth;

  // Apply the same side-only convenience fallback for right, bottom, and left.
  const borderRightWidth =
    !props?.borderRightWidth &&
    props.borderRightColour !== undefined &&
    props.borderRightColour !== 'none'
      ? 'sm'
      : props.borderRightWidth;

  const borderBottomWidth =
    !props?.borderBottomWidth &&
    props.borderBottomColour !== undefined &&
    props.borderBottomColour !== 'none'
      ? 'sm'
      : props.borderBottomWidth;

  const borderLeftWidth =
    !props?.borderLeftWidth &&
    props.borderLeftColour !== undefined &&
    props.borderLeftColour !== 'none'
      ? 'sm'
      : props.borderLeftWidth;

  const stylesProp: React.CSSProperties = {
    width: width === 'full' ? '100%' : (width as CSSProperties['width']),
    height: height === 'full' ? '100%' : height,
    minHeight,
    zIndex,
    top,
    right,
    bottom,
    left,
    ...maxWidthVars,
    ...minWidthVars,
    ...maxHeightVars,
    ...flexBasisVars,
    overflow: props.maxHeight || props.maxWidth ? 'auto' : undefined,
    overflowWrap,
    wordBreak,
  };

  // Apply list reset styles for list items
  const listResetClass = listVariants({
    as: Component === 'li' ? 'li' : undefined,
  });

  return (
    <Component
      className={[
        styles.box,
        borderVariants({
          ...props,
          borderWidth,
          borderTopWidth,
          borderRightWidth,
          borderBottomWidth,
          borderLeftWidth,
        }),
        flexBaseClass,
        paddingBaseClass,
        marginBaseClass,
        backgroundColorVariants({
          backgroundColor: props.backgroundColor,
          ...props,
        }),
        positionVariants({ position }),
        listResetClass,
        boxSizingVariants({ boxSizing }),
        ...justifyContentResponsiveClasses,
        ...flexDirectionResponsiveClasses,
        ...gapResponsiveClasses,
        ...rowGapResponsiveClasses,
        ...columnGapResponsiveClasses,
        ...alignItemsResponsiveClasses,
        ...paddingTopResponsiveClasses,
        ...paddingRightResponsiveClasses,
        ...paddingBottomResponsiveClasses,
        ...paddingLeftResponsiveClasses,
        ...paddingResponsiveClasses,
        ...paddingXResponsiveClasses,
        ...paddingYResponsiveClasses,
        ...marginTopResponsiveClasses,
        ...marginRightResponsiveClasses,
        ...marginBottomResponsiveClasses,
        ...marginLeftResponsiveClasses,
        ...marginResponsiveClasses,
        ...marginXResponsiveClasses,
        ...marginYResponsiveClasses,
      ]
        .filter(Boolean)
        .join(' ')}
      style={stylesProp}
      data-testid={dataTestIdNative ?? dataTestIdLegacy}
    >
      {children}
    </Component>
  );
};

export default Box;
